import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import { addComment, createIssue, deleteIssue, ISSUE_PRIORITIES, ISSUE_SEVERITIES, ISSUE_STATUSES, ISSUE_TYPES, updateIssue } from "@/lib/issues.functions";
import { issueQueryOptions, issuesQueryOptions, labelOf, PRIORITY_COLORS, STATUS_COLORS, type IssueRow } from "@/lib/issues.queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Field } from "@/components/admin/Field";
import { FormDialog, FormSection, SaveButton, useDirty } from "@/components/admin/FormDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Archive, Bug, ExternalLink, Pencil, Plus, Search, Trash2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/issues")({
  component: IssuesAdminPage,
});

function IssuesAdminPage() {
  const qc = useQueryClient();
  const { data: issues = [] } = useQuery(issuesQueryOptions());
  const update = useServerFn(updateIssue);
  const del = useServerFn(deleteIssue);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["issues"] });

  const archiveMut = useMutation({
    mutationFn: async (id: string) => update({ data: { id, patch: { status: "archived" } } }),
    onSuccess: () => { invalidate(); toast.success("Archived"); },
    onError: (e: any) => toast.error(e.message),
  });
  const deleteMut = useMutation({
    mutationFn: async (id: string) => del({ data: { id } }),
    onSuccess: () => { invalidate(); toast.success("Deleted"); setDeleteTarget(null); },
    onError: (e: any) => toast.error(e.message),
  });

  const [newOpen, setNewOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IssueRow | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const filtered = useMemo(() => {
    return issues.filter((i) => {
      if (!showArchived && i.status === "archived") return false;
      if (filterStatus !== "all" && i.status !== filterStatus) return false;
      if (filterType !== "all" && i.type !== filterType) return false;
      if (search && !`${i.title} ${i.ticket_number} ${i.section ?? ""}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [issues, filterStatus, filterType, search, showArchived]);

  const openCount = issues.filter((i) => !["completed", "closed", "rejected", "archived"].includes(i.status)).length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Issues</h1>
          <p className="text-muted-foreground">{openCount} open / {issues.length} total</p>
        </div>
        <Button onClick={() => setNewOpen(true)}><Plus className="w-4 h-4 mr-2" />New Issue</Button>
        <NewIssueDialog
          open={newOpen}
          onOpenChange={setNewOpen}
          onDone={() => { setNewOpen(false); invalidate(); }}
        />
      </div>

      <Card>
        <CardHeader><CardTitle>Filters</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label>Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-3 text-muted-foreground" />
              <Input className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Title, ticket #, section" />
            </div>
          </div>
          <div className="w-44">
            <Label>Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {ISSUE_STATUSES.map((s) => <SelectItem key={s} value={s}>{labelOf(s)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <Label>Type</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {ISSUE_TYPES.map((t) => <SelectItem key={t} value={t}>{labelOf(t)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 h-10 cursor-pointer select-none">
            <Checkbox checked={showArchived} onCheckedChange={(v) => setShowArchived(!!v)} id="show-archived" />
            <span className="text-sm">Show archived</span>
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reported</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No issues</TableCell></TableRow>
              )}
              {filtered.map((i: IssueRow) => (
                <TableRow key={i.id} className={`hover:bg-muted/40 ${i.status === "archived" ? "opacity-60" : ""}`}>
                  <TableCell>
                    <button onClick={() => setEditId(i.id)} className="text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">{i.ticket_number}</span>
                        <span className="font-medium hover:underline">{i.title}</span>
                      </div>
                      {i.section && <div className="text-xs text-muted-foreground">{i.section}</div>}
                    </button>
                  </TableCell>
                  <TableCell><Badge variant="outline">{labelOf(i.type)}</Badge></TableCell>
                  <TableCell><Badge className={PRIORITY_COLORS[i.priority]}>{labelOf(i.priority)}</Badge></TableCell>
                  <TableCell><Badge className={STATUS_COLORS[i.status]}>{labelOf(i.status)}</Badge></TableCell>
                  <TableCell className="text-xs">{new Date(i.reported_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" title="Edit" onClick={() => setEditId(i.id)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" title="Archive" disabled={i.status === "archived" || archiveMut.isPending}
                        onClick={() => archiveMut.mutate(i.id)}>
                        <Archive className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" title="Delete" className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(i)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editId && <EditIssueDialog id={editId} onClose={() => setEditId(null)} />}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete issue {deleteTarget?.ticket_number} — "{deleteTarget?.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditIssueDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: issue, isLoading } = useQuery(issueQueryOptions(id));
  const update = useServerFn(updateIssue);
  const comment = useServerFn(addComment);
  const [commentBody, setCommentBody] = useState("");

  const commentMut = useMutation({
    mutationFn: async () => comment({ data: { issue_id: id, body: commentBody } }),
    onSuccess: () => { setCommentBody(""); qc.invalidateQueries({ queryKey: ["issues", id] }); toast.success("Comment added"); },
    onError: (e: any) => toast.error(e.message),
  });

  const [form, setForm] = useState<any>(null);
  // The dialog is mounted only while open; snapshot once the issue has loaded.
  const dirty = useDirty(form, form !== null);
  useEffect(() => {
    if (issue) {
      setForm({
        title: issue.title,
        description: issue.description ?? "",
        type: issue.type,
        section: issue.section ?? "",
        severity: issue.severity,
        priority: issue.priority,
        status: issue.status,
        due_date: issue.due_date ? issue.due_date.split("T")[0] : "",
        engineering_estimate_hours: issue.engineering_estimate_hours ?? "",
      });
    }
  }, [issue]);

  const saveMut = useMutation({
    mutationFn: async () =>
      update({
        data: {
          id,
          patch: {
            title: form.title,
            description: form.description || null,
            type: form.type,
            section: form.section || null,
            severity: form.severity,
            priority: form.priority,
            status: form.status,
            due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
            engineering_estimate_hours: form.engineering_estimate_hours === "" ? null : Number(form.engineering_estimate_hours),
          },
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["issues"] });
      qc.invalidateQueries({ queryKey: ["issues", id] });
      toast.success("Saved");
      onClose();
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      size="lg"
      icon={Bug}
      title={
        <>
          <span className="mr-2 font-mono text-sm text-muted-foreground">{issue?.ticket_number}</span>
          Edit Issue
        </>
      }
      context={
        issue ? (
          <>
            {issue.title}
            {" · "}
            <Link to="/admin/issues/$id" params={{ id }} className="text-primary hover:underline">
              Open full view <ExternalLink className="inline h-3 w-3" />
            </Link>
          </>
        ) : (
          "Loading…"
        )
      }
      dirty={dirty}
      busy={saveMut.isPending}
      asForm
      onSubmit={() => {
        if (form) saveMut.mutate();
      }}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <SaveButton dirty={dirty} pending={saveMut.isPending} disabled={!form} label="Save Changes" />
        </>
      }
    >
      {isLoading || !form ? (
        <div className="p-8 text-center text-muted-foreground">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-4">
            <FormSection title="Issue" cols={1}>
              <Field label="Title">
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </Field>
              <Field label="Description">
                <Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Field>
            </FormSection>
            <FormSection title="Triage">
              <Field label="Type">
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ISSUE_TYPES.map((t) => <SelectItem key={t} value={t}>{labelOf(t)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Section">
                <Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} />
              </Field>
              <Field label="Severity">
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ISSUE_SEVERITIES.map((s) => <SelectItem key={s} value={s}>{labelOf(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Priority">
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ISSUE_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{labelOf(p)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ISSUE_STATUSES.map((s) => <SelectItem key={s} value={s}>{labelOf(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Due Date">
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </Field>
              <Field label="Engineering Estimate (hours)" full>
                <Input type="number" step="0.25" value={form.engineering_estimate_hours}
                  onChange={(e) => setForm({ ...form, engineering_estimate_hours: e.target.value })} />
              </Field>
              <div className="text-xs text-muted-foreground space-y-1 sm:col-span-2">
                <div>Reported: {new Date(issue!.reported_at).toLocaleString()} by {issue!.reporter_email ?? "—"}</div>
                {issue!.accepted_at && <div>Accepted: {new Date(issue!.accepted_at).toLocaleString()}</div>}
                {issue!.completed_at && <div>Completed: {new Date(issue!.completed_at).toLocaleString()}</div>}
              </div>
            </FormSection>
          </div>

          <div className="space-y-4">
            <FormSection title="History" cols={1}>
            <div className="border rounded p-3 max-h-[320px] overflow-y-auto space-y-3 bg-muted/30">
              {(issue!.history?.length ?? 0) === 0 && (
                <div className="text-xs text-muted-foreground">No history recorded yet. Future changes will be timestamped here automatically.</div>
              )}
              {issue!.history?.map((h: any) => (
                <div key={h.id} className="text-xs border-l-2 border-primary/40 pl-2">
                  <div className="font-medium">{labelOf(h.event_type)}{h.field_name ? ` · ${h.field_name}` : ""}</div>
                  {(h.from_value || h.to_value) && (
                    <div className="text-muted-foreground break-all">
                      {h.from_value && <span className="line-through">{h.from_value}</span>}
                      {h.from_value && h.to_value && " → "}
                      {h.to_value && <span>{h.to_value}</span>}
                    </div>
                  )}
                  {h.notes && <div className="italic text-muted-foreground">{h.notes}</div>}
                  <div className="text-muted-foreground">{h.actor_email ?? "system"} · {new Date(h.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
            </FormSection>

            <FormSection title="Comments" cols={1}>
            <div className="border rounded p-3 max-h-[240px] overflow-y-auto space-y-3 bg-muted/30">
              {(issue!.comments?.length ?? 0) === 0 && (
                <div className="text-xs text-muted-foreground">No comments yet.</div>
              )}
              {issue!.comments?.map((c: any) => (
                <div key={c.id} className="text-xs border-l-2 border-primary/40 pl-2">
                  <div className="text-muted-foreground">{c.author_email ?? "Unknown"} · {new Date(c.created_at).toLocaleString()}</div>
                  <div className="whitespace-pre-wrap">{c.body}</div>
                </div>
              ))}
            </div>
            <Textarea rows={3} value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder="Add a comment…" />
            <Button type="button" size="sm" onClick={() => commentMut.mutate()} disabled={!commentBody.trim() || commentMut.isPending}>
              Post Comment
            </Button>
            </FormSection>
          </div>
        </div>
      )}
    </FormDialog>
  );
}

const NEW_ISSUE = {
  title: "",
  description: "",
  type: "problem_bug" as (typeof ISSUE_TYPES)[number],
  section: "",
  severity: "medium" as (typeof ISSUE_SEVERITIES)[number],
  priority: "normal" as (typeof ISSUE_PRIORITIES)[number],
  due_date: "",
};

function NewIssueDialog({
  open,
  onOpenChange,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const create = useServerFn(createIssue);
  const [form, setForm] = useState(NEW_ISSUE);
  useEffect(() => {
    if (open) setForm(NEW_ISSUE);
  }, [open]);
  const dirty = useDirty(form, open);

  const mut = useMutation({
    mutationFn: async () =>
      create({
        data: {
          title: form.title,
          description: form.description || null,
          type: form.type,
          section: form.section || null,
          severity: form.severity,
          priority: form.priority,
          due_date: form.due_date || null,
        },
      }),
    onSuccess: () => { toast.success("Issue created"); onDone(); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      icon={Bug}
      title="New Issue"
      context="Report a problem or request"
      dirty={dirty}
      busy={mut.isPending}
      asForm
      onSubmit={() => {
        if (form.title) mut.mutate();
      }}
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <SaveButton dirty={dirty} pending={mut.isPending} disabled={!form.title} label="Create" pendingLabel="Creating…" />
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Label>Title *</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v: any) => setForm({ ...form, type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ISSUE_TYPES.map((t) => <SelectItem key={t} value={t}>{labelOf(t)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Section</Label>
            <Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} placeholder="e.g. Home, Board, Insights" />
          </div>
          <div>
            <Label>Severity</Label>
            <Select value={form.severity} onValueChange={(v: any) => setForm({ ...form, severity: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ISSUE_SEVERITIES.map((s) => <SelectItem key={s} value={s}>{labelOf(s)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v: any) => setForm({ ...form, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ISSUE_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{labelOf(p)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Due Date</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </div>
    </FormDialog>
  );
}
